import { Logic } from "@cirrus/fe-lib"
import { makeStyles } from "@mui/styles"
import { flow } from "fp-ts/es6/function"
import { and, or } from "ramda"

import { Css } from "../../../lib/_public.js"

import * as lib from "./lib.js"

export const useRootOverrides = makeStyles(({ typography }) => ({
  root: lib.whenLegacy(
    () => ({ flexDirection: "column", width: "100%" }),

    flow(lib.withHeight(typography), ({ height, rootHeight, width }) => ({
      flexDirection: "column",
      minHeight: or(rootHeight, height),
      width,
    })),
  ),
}))

export const useFormHelperOverrides = makeStyles(({ typography }) => ({
  root: {
    "&.MuiFormHelperText-root": {
      marginLeft: 0,
    },
  },
}))

export const useInputOverrides = makeStyles(({ typography, withPalette }) => ({
  input: flow(lib.withFontSize(typography), lib.withHeight(typography), ({ fontSize, height }) => ({
    fontSize,
    height,
  })),

  root: flow(
    lib.withFontSize(typography),
    lib.withHeight(typography),

    ({
      background,
      backgroundOnHover,
      disableUnderline,
      fontSize,
      height,
      hiddenLabel,
      rootHeight,
    }) => {
      return {
        "&[class*='MuiInputBase-root']": {
          "&:hover": {
            background: withPalette(backgroundOnHover),
            backgroundColor: withPalette(backgroundOnHover),
          },
          "&[class*='MuiFilledInput-root']": {
            fontSize,
            paddingTop: Logic.consequence(0, hiddenLabel),
          },
          background: withPalette(background),
          backgroundColor: withPalette(background),
          borderRadius: and(disableUnderline, 4),
          height,
          minHeight: rootHeight,
          width: "inherit",
        },
      }
    },
  ),

  underline: {
    "&:after": {
      borderColor: withPalette("info", "light"),
    },
    "&[class*='Mui-error']:after": {
      "&:after": {
        borderWidth: Css.important(0),
      },
      "&:after": {
        borderWidth: "2pt",
      },
      "&:before": {
        borderWidth: Css.important(0),
      },
      "&:hover:before": {
        borderWidth: Css.important(0),
      },
      borderColor: withPalette("error", "light"),
    },
    "&[class*='Mui-focused']:after": {
      borderColor: withPalette("info", "light"),
    },
  },
}))

export const useOutlinedInputOverrides = makeStyles(({ typography, withPalette }) => ({
  disabled: lib.whenLegacy(() => ({
    "& *": {
      cursor: Css.important("not-allowed"),
    },
    "&[class*='MuiOutlinedInput-root']": {
      backgroundColor: "#f3f3f3",
      borderColor: "#e9e9e9",
      cursor: Css.important("not-allowed"),
    },
  })),

  error: lib.whenLegacy(() => ({
    "&$error $notchedOutline": {
      borderColor: Css.important("#cf4444"),
    },
  })),

  focused: lib.whenLegacy(() => ({
    "&:not($error)$focused $notchedOutline": {
      borderColor: Css.important("#287ec7"),
    },
  })),

  notchedOutline: lib.whenLegacy(() => ({
    "&.MuiOutlinedInput-notchedOutline": {
      border: "2px solid #dedce0",
      borderRadius: 4,
    },
  })),

  root: lib.whenLegacy(
    () => ({
      "&$disabled $notchedOutline": {
        borderColor: "#e9e9e9",
      },

      "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
        borderColor: "#dedce0",
      },

      backgroundColor: "#fff",
      minHeight: 40,
      width: "100%",
    }),

    flow(
      lib.withHeight(typography),

      ({ background, backgroundOnHover, disableUnderline, height }) => ({
        "&:hover": {
          background: withPalette(backgroundOnHover),
          backgroundColor: withPalette(backgroundOnHover),
        },

        background: withPalette(background),
        backgroundColor: withPalette(background),
        borderRadius: and(disableUnderline, 4),
        height,
        width: "inherit",
      }),
    ),
  ),
}))

export const useInputLabelOverrides = makeStyles(({ palette }) => ({
  formControl: {
    "&[class*='Mui-focused'][class*='MuiFormLabel-root'], &[class*='Mui-focused'] > [class*='MuiFormLabel-asterisk'][class*='Mui-error']":
      {
        color: palette.info.main,
      },
  },
}))
